import { Link } from 'react-router-dom'
import { 
  GraduationCap, 
  Users, 
  BookOpen, 
  MessageCircle, 
  Bell, 
  TrendingUp,
  Calendar,
  Award,
  ArrowLeft
} from 'lucide-react'

export default function TeacherDemo() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Demo Banner */}
      <div className="bg-teal-600 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium">Demo Mode</span>
            <span className="text-sm">You're viewing the Teacher dashboard</span>
          </div>
          <Link to="/" className="text-sm hover:underline flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">SchoolConnect</span>
            </div>
            <div className="flex items-center gap-4">
              <Bell className="w-5 h-5 text-gray-500" />
              <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-teal-700 font-semibold text-sm">MJ</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Welcome back, Mrs. Johnson!</h1>
          <p className="text-gray-600">Grade 5 Teacher • Sunshine Primary School</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">28</p>
                <p className="text-sm text-gray-500">Students</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">12</p>
                <p className="text-sm text-gray-500">New Messages</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">156</p>
                <p className="text-sm text-gray-500">Points Awarded</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">3</p>
                <p className="text-sm text-gray-500">Events This Week</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 gap-4">
                <button className="p-4 bg-teal-50 rounded-xl text-left hover:bg-teal-100 transition-colors">
                  <MessageCircle className="w-6 h-6 text-teal-600 mb-2" />
                  <p className="font-medium text-gray-900">Message Parents</p>
                  <p className="text-sm text-gray-500">Send updates to parents</p>
                </button>
                <button className="p-4 bg-blue-50 rounded-xl text-left hover:bg-blue-100 transition-colors">
                  <Award className="w-6 h-6 text-blue-600 mb-2" />
                  <p className="font-medium text-gray-900">Award Points</p>
                  <p className="text-sm text-gray-500">Recognize student achievements</p>
                </button>
                <button className="p-4 bg-purple-50 rounded-xl text-left hover:bg-purple-100 transition-colors">
                  <Bell className="w-6 h-6 text-purple-600 mb-2" />
                  <p className="font-medium text-gray-900">Post Announcement</p>
                  <p className="text-sm text-gray-500">Share class updates</p>
                </button>
                <button className="p-4 bg-orange-50 rounded-xl text-left hover:bg-orange-100 transition-colors">
                  <BookOpen className="w-6 h-6 text-orange-600 mb-2" />
                  <p className="font-medium text-gray-900">View Portfolio</p>
                  <p className="text-sm text-gray-500">Check student work</p>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Emma Johnson earned 50 points</p>
                    <p className="text-sm text-gray-500">For helping a classmate with math homework</p>
                    <p className="text-xs text-gray-400 mt-1">2 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">New message from Sarah's parent</p>
                    <p className="text-sm text-gray-500">Question about tomorrow's field trip</p>
                    <p className="text-xs text-gray-400 mt-1">4 hours ago</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Class 5B completed science project</p>
                    <p className="text-sm text-gray-500">95% of students submitted their work</p>
                    <p className="text-xs text-gray-400 mt-1">Yesterday</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Class List */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">My Classes</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-teal-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">Grade 5B</p>
                    <p className="text-sm text-gray-500">28 students</p>
                  </div>
                  <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-xs">Active</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">Grade 5A</p>
                    <p className="text-sm text-gray-500">26 students</p>
                  </div>
                  <span className="bg-gray-400 text-white px-3 py-1 rounded-full text-xs">Inactive</span>
                </div>
              </div>
            </div>

            {/* Top Students */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Students This Week</h2>
              <div className="space-y-3">
                {[
                  { name: 'Emma Johnson', points: 450 },
                  { name: 'Lucas Smith', points: 380 },
                  { name: 'Olivia Brown', points: 350 },
                  { name: 'Noah Davis', points: 320 },
                ].map((student, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-700 font-semibold text-sm">
                        {idx + 1}
                      </div>
                      <p className="font-medium text-gray-900">{student.name}</p>
                    </div>
                    <span className="text-teal-600 font-semibold">{student.points} pts</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA to Sign Up */}
      <div className="bg-teal-900 py-12 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to transform your classroom?</h2>
          <p className="text-teal-100 mb-6">Start your 14-day free trial today. No credit card required.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register-school" className="bg-white text-teal-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors">
              Start 14-Day Free Trial
            </Link>
            <Link to="/demo/parent" className="bg-white/10 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/20 transition-colors">
              See Parent View
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
